import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Divider,
  TextField,
  Button,
  Stack,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import {
  FavoriteBorder,
  Favorite,
  CommentOutlined,
  MoreVert,
  SendRounded,
  DeleteOutlineRounded,
} from "@mui/icons-material";
import { addCommentToPost, addLikeToPost, deletePost } from "../utils/api";
import TimeAgo from "../TimeAgo";
import { Link } from "react-router-dom";
import Comment from "./Comment";
import { useSelector, useDispatch } from "react-redux";
import { likePost, commentOnPost, deletePostReducer } from "../features/postsSlice";

const Post = ({ post }) => {
  const dispatch = useDispatch();
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState("");
  const [isLiked, setIsLiked] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const user = useSelector((state) => state.auth.user);
  const allUsers = useSelector((state) => state.users.users);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const postAuthor = allUsers.find((u) => u._id === post.author._id);

  const renderedComments = useMemo(
    () => post.comments.map((c) => <Comment key={c._id} comment={c} />),
    [post.comments]
  );

  useEffect(() => {
    if (user && isAuthenticated) {
      setIsLiked(post.likes.includes(user._id));
    }
  }, [user, post.likes, isAuthenticated]);

  if (!postAuthor) return null;

  const handleLikeClick = async () => {
    try {
      setIsLiked((prev) => !prev);
      await addLikeToPost(post._id, user._id);
      dispatch(likePost({ userId: user._id, postId: post._id }));
    } catch (error) {
      console.error("Failed to like post:", error);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    try {
      await addCommentToPost(post._id, user._id, comment);
      dispatch(commentOnPost({ postId: post._id, userId: user._id, comment }));
      setComment("");
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  const handleDeletePost = async () => {
    setMenuAnchor(null);
    try {
      await deletePost(post._id);
      dispatch(deletePostReducer({ postId: post._id }));
    } catch (error) {
      console.error("Error deleting post:", error);
    }
  };

  const initials = `${postAuthor?.firstName?.[0] ?? ""}${postAuthor?.lastName?.[0] ?? ""}`.toUpperCase();
  const currentUserInitials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase()
    : "";

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "14px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.09)",
        mb: 2,
        overflow: "hidden",
        width: "100%",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.13)" },
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", px: 2, pt: 2, pb: 1.5, gap: 1.5 }}>
        <Link to={`/profile/${postAuthor._id}`} style={{ textDecoration: "none", flexShrink: 0 }}>
          <Avatar
            src={postAuthor?.profilePicture}
            sx={{
              width: 42,
              height: 42,
              border: "2px solid #F3F4F6",
              bgcolor: "#D4AF37",
              color: "#1A1A2E",
              fontWeight: 700,
              fontSize: 14,
              transition: "transform 0.18s",
              "&:hover": { transform: "scale(1.06)" },
            }}
          >
            {initials}
          </Avatar>
        </Link>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Link
            to={`/profile/${postAuthor._id}`}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 14.5,
                color: "#111827",
                lineHeight: 1.2,
                "&:hover": { color: "#D4AF37" },
                transition: "color 0.15s",
              }}
            >
              {postAuthor?.firstName} {postAuthor?.lastName}
            </Typography>
          </Link>
          <Typography sx={{ color: "#9CA3AF", fontSize: 12, mt: 0.2 }}>
            <TimeAgo createdAt={post.createdAt} />
          </Typography>
        </Box>

        {/* More options */}
        {isAuthenticated && user._id === postAuthor?._id && (
          <>
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ color: "#9CA3AF", "&:hover": { color: "#1A1A2E" } }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              PaperProps={{
                elevation: 3,
                sx: {
                  borderRadius: "10px",
                  minWidth: 140,
                  mt: 0.5,
                  "& .MuiMenuItem-root": {
                    fontSize: 14,
                    borderRadius: "6px",
                    mx: 0.5,
                    px: 1.5,
                  },
                },
              }}
            >
              <MenuItem
                onClick={handleDeletePost}
                sx={{ color: "#EF4444", gap: 1 }}
              >
                <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                Delete post
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      {/* Content */}
      <Typography
        sx={{
          px: 2,
          pb: post.image ? 1.5 : 2,
          fontSize: 14.5,
          color: "#1F2937",
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
        }}
      >
        {post.content}
      </Typography>

      {/* Image */}
      {post.image && (
        <Box sx={{ px: 0, pb: 0 }}>
          <img
            src={`http://localhost:5000${post.image}`}
            alt="Post"
            style={{ width: "100%", maxHeight: 420, objectFit: "cover", display: "block" }}
          />
        </Box>
      )}

      {/* Action bar */}
      <Box sx={{ px: 2, py: 0.5 }}>
        <Divider sx={{ mb: 0.5 }} />
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton
            onClick={handleLikeClick}
            disabled={!isAuthenticated}
            size="small"
            sx={{
              color: isLiked ? "#EF4444" : "#6B7280",
              transition: "all 0.18s",
              "&:hover": { color: "#EF4444", bgcolor: "rgba(239,68,68,0.08)" },
              "&.Mui-disabled": { opacity: 0.4 },
            }}
          >
            {isLiked ? (
              <Favorite sx={{ fontSize: 19 }} />
            ) : (
              <FavoriteBorder sx={{ fontSize: 19 }} />
            )}
          </IconButton>
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: "#6B7280", minWidth: 16 }}
          >
            {post.likes?.length ?? 0}
          </Typography>

          <IconButton
            onClick={() => setShowCommentBox((p) => !p)}
            disabled={!isAuthenticated}
            size="small"
            sx={{
              ml: 0.5,
              color: showCommentBox ? "#1A1A2E" : "#6B7280",
              transition: "all 0.18s",
              "&:hover": { color: "#1A1A2E", bgcolor: "rgba(26,26,46,0.07)" },
              "&.Mui-disabled": { opacity: 0.4 },
            }}
          >
            <CommentOutlined sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: "#6B7280", minWidth: 16 }}
          >
            {post.comments?.length ?? 0}
          </Typography>
        </Stack>
      </Box>

      {/* Comment section */}
      {showCommentBox && (
        <Box sx={{ px: 2, pb: 2 }}>
          {/* Comment input */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}>
            <Avatar
              src={user?.profilePicture}
              sx={{
                width: 32,
                height: 32,
                bgcolor: "#D4AF37",
                color: "#1A1A2E",
                fontSize: 11,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {currentUserInitials}
            </Avatar>
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                gap: 1,
                bgcolor: "#F8F9FA",
                borderRadius: "24px",
                px: 2,
                py: 0.5,
                border: "1px solid #E5E7EB",
                alignItems: "center",
                "&:focus-within": { borderColor: "#1A1A2E" },
                transition: "border-color 0.18s",
              }}
            >
              <TextField
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                placeholder="Write a comment…"
                variant="standard"
                fullWidth
                InputProps={{ disableUnderline: true }}
                sx={{ "& input": { fontSize: 13.5, py: 0.5 } }}
              />
              <IconButton
                size="small"
                onClick={handleAddComment}
                disabled={!comment.trim()}
                sx={{
                  color: "#D4AF37",
                  flexShrink: 0,
                  "&:hover": { color: "#b8941f" },
                  "&.Mui-disabled": { opacity: 0.3 },
                }}
              >
                <SendRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Comments list */}
          {renderedComments}
        </Box>
      )}
    </Box>
  );
};

export default Post;
